import { CheckIcon, CopyIcon, EyeIcon, Share2Icon, UserPlusIcon, WandIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { ApiError, createRoomInvite } from "../lib/api";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * Zaproszenie do pokoju.
 *
 * Powstało, bo **nie było jak zaprosić kogokolwiek**: trasa serwera
 * (`POST /rooms/:slug/invites`) i ekran wejścia (`/z/:code`) istniały od M5,
 * a wywołanie w kliencie było martwym kodem — nikt go nie wołał. Wspólne
 * oglądanie bez sposobu udostępnienia pokoju jest funkcją dla jednej osoby.
 *
 * **Rola jest wyborem, nie domyślną.** Zaproszenie „tylko oglądanie" i „może
 * prowadzić" to dwie różne decyzje: druga oddaje obcej osobie pauzę
 * i przewijanie dla wszystkich w pokoju. Jedno kliknięcie na jedno i drugie,
 * ale nazwane wprost.
 *
 * Na dotyku używamy systemowego udostępniania (`navigator.share`), bo tam link
 * i tak trafia do komunikatora, a schowek jest tylko przystankiem. Na desktopie
 * kopiujemy do schowka — okno wyboru aplikacji byłoby tam zbędnym krokiem.
 */

type Role = "viewer" | "controller";

export function RoomInvite({ slug, roomName }: { slug: string; roomName: string }) {
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState<Role | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invite = useCallback(
    async (role: Role) => {
      setBusy(role);
      setError(null);
      try {
        const created = await createRoomInvite(slug, { role });
        setLink(created.url);
        await handOff(created.url, roomName);
        setCopied(true);
        // Ptaszek gaśnie sam: to potwierdzenie chwili, nie stan pokoju.
        setTimeout(() => setCopied(false), 2500);
      } catch (e: unknown) {
        setError(e instanceof ApiError ? e.message : "Nie udało się utworzyć zaproszenia");
      } finally {
        setBusy(null);
      }
    },
    [slug, roomName],
  );

  return (
    <div className="flex flex-col items-end gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="hero" size="sm">
            <UserPlusIcon />
            Zaproś
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-[min(20rem,90vw)]">
          <DropdownMenuLabel>Link działa 24 godziny</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem
            disabled={busy !== null}
            onSelect={(e) => {
              // Menu ma zostać otwarte do czasu odpowiedzi serwera — inaczej
              // zamknęłoby się przed pokazaniem, czy cokolwiek się udało.
              e.preventDefault();
              void invite("viewer");
            }}
          >
            <EyeIcon />
            <span className="flex flex-col">
              <span>Tylko oglądanie</span>
              <span className="text-xs text-muted-foreground">
                Gość widzi obraz i czat, nie zmienia odtwarzania.
              </span>
            </span>
          </DropdownMenuItem>

          <DropdownMenuItem
            disabled={busy !== null}
            onSelect={(e) => {
              e.preventDefault();
              void invite("controller");
            }}
          >
            <WandIcon />
            <span className="flex flex-col">
              <span>Może prowadzić</span>
              <span className="text-xs text-muted-foreground">
                Gość może przejąć prowadzenie: pauza i przewijanie dla wszystkich.
              </span>
            </span>
          </DropdownMenuItem>

          {link !== null && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  void copyToClipboard(link).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2500);
                  });
                }}
              >
                {copied ? <CheckIcon className="text-success" /> : <CopyIcon />}
                <span className="min-w-0 truncate font-mono text-xs">{link}</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Potwierdzenie POD przyciskiem, nie w menu: menu zamyka się po wyborze,
          a wtedy zniknęłaby jedyna informacja, że link jest już w schowku. */}
      {copied && (
        <span className="flex items-center gap-1 text-xs text-success">
          <Share2Icon className="size-3" />
          link skopiowany
        </span>
      )}
      {error !== null && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

/**
 * Oddaje link tam, gdzie na danym urządzeniu ma trafić.
 *
 * Na telefonie systemowe udostępnianie prowadzi wprost do komunikatora, więc
 * schowek byłby przystankiem bez powodu. Na desktopie odwrotnie: okno wyboru
 * aplikacji jest krokiem, którego nikt nie chce, a wklejenie i tak nastąpi ręcznie.
 */
async function handOff(url: string, roomName: string): Promise<void> {
  const share = navigator.share?.bind(navigator);
  const coarse = window.matchMedia("(pointer: coarse)").matches;

  if (share !== undefined && coarse) {
    try {
      await share({ title: `Anisync: ${roomName}`, text: "Oglądajmy razem", url });
      return;
    } catch {
      // Odrzucenie okna systemowego to normalna decyzja użytkownika, nie awaria —
      // wtedy zostaje schowek.
    }
  }
  await copyToClipboard(url);
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Schowek bywa odcięty (brak zgody, kontekst bez HTTPS). Link jest widoczny
    // w menu, więc da się go zaznaczyć ręcznie — cichy brak jest tu lepszy od
    // komunikatu o czymś, na co użytkownik nie ma wpływu.
  }
}
